export const mode = "PA";

// Which approach guards the map from hijacking page scroll on load:
// - "cooperative": Mapbox's built-in cooperativeGestures — map stays
//   scroll-zoomable only while holding Ctrl/Cmd, and shows its own brief
//   explanatory overlay when a bare scroll is blocked.
// - "click-to-activate": this app's own gate — scroll-zoom (and only
//   scroll-zoom; dragging/panning is never blocked) stays off until the
//   user clicks anywhere on the map once, then it's unlocked for good.
// - "off": neither guard applies — default Mapbox behavior, scroll-zoom
//   is always on.
// Flip this to compare the three.
export const scrollZoomGuard = "off";

export const mapStyles = {
  schoolLayerStrokeColor: "#FFFFFF",
  stateLayerStrokeColor: "#a6a8ab",
  stateLayerHoverColor: "#FFFFFF",
  stateCountTier1Color: "#EDEBEC",
  stateCountTier2Color: "#D1D1D1",
  stateCountTier3Color: "#CBEBF5",
  stateCountTier4Color: "#90D7F0",
  stateCountTier5Color: "#50AFE2",
  stateCountTextColor: "#002B43",
  clusterTier1Color: "#EDEBEC",
  clusterTier2Color: "#CBEBF5",
  clusterTier3Color: "#90D7F0",
  clusterTier4Color: "#50AFE2",
  clusterTier5Color: "#406DB5",
  legendTier1Color: "#EDEBEC",
  legendTier2Color: "#CBEBF5",
  legendTier3Color: "#90D7F0",
  legendTier4Color: "#50AFE2",
  legendTier5Color: "#406DB5",
  legendTier6Color: "#2E308A",
  legendTier7Color: "#002B43",
  legendDistrictTier1Color: "#FFFFFF",
};

export const mapStyle =
  "mapbox://styles/generation180/cmshipu3d00wr01qodkwd5gvi";

export const mapboxToken =
  "pk.eyJ1IjoiZ2VuZXJhdGlvbjE4MCIsImEiOiJjamFiYzV0eDgxMHJ4MnFvNnpjbGFjZzd3In0.38-VoxnVT4VcOwsi46mAOA";

export const initialBounds = [
  [-132, 24],
  [-62, 51],
];

const baseConfig = {
  itchWithRotate: false,
  dragRotate: false,
  touchZoomRotate: false,
  touchRotate: false,
  zoomAnimation: false,
  minZoom: 2,
  padding: { top: 50, bottom: 40, left: 20, right: 20 },
  style: {
    glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf",
  }
}

// + 39.7425	– 075.0450	+ 37.8713	– 079.4938
// maxBounds boxes below are kept roughly square (rather than tightly
// hugging each state) so mapbox's aspect-ratio-derived minimum zoom for
// maxBounds doesn't fight fitBounds(getStateBounds(stateName)) on narrow
// viewports — see mapConfig.maxBounds below for the full explanation.

export const mdMapConfig = {
  ...baseConfig,
  longitude: -77.5,
  latitude: 38.7,
  zoom: 6.5,
  mobileZoom: 6,
  maxBounds: [
    [-89.5, 26.7], //Southwest Left (lower), Bottom (lower)
    [-65.5, 50.7], //Northeast Right (higher), Top (higher)
  ]
};

export const paMapConfig = {
  ...baseConfig,
  longitude: -77.75,
  latitude: 40.98757700324566,
  zoom: 6.6,
  mobileZoom: 5.5,
  maxBounds: [
    [-90.75, 28], //Southwest Left (lower), Bottom (lower)
    [-64.75, 54], //Northeast Right (higher), Top (higher)
  ]
};

export const vaMapConfig = {
  ...baseConfig,
  longitude: -79,
  latitude: 38,
  zoom: 6,
  mobileZoom: 5.2,
  maxBounds: [
    [-92, 25], //Southwest Left (lower), Bottom (lower)
    [-66, 51], //Northeast Right (higher), Top (higher)
  ]
};

export const mapConfig = {
  ...baseConfig,
  longitude: -96,
  latitude: 40,
  zoom: 3.5,
  padding: {top: 50, bottom: 40, left: 20, right: 20},
  initialBounds,
  // Loose world bounds rather than a tight box around the US: mapbox derives
  // a minimum zoom from maxBounds' own aspect ratio to keep it filling the
  // viewport, and a landscape-shaped box here fights fitBounds(initialBounds)
  // on portrait/narrow viewports, forcing a zoomed-in initial view.
  maxBounds: [
    [-180, -85], //Southwest
    [180, 85], //Northeast
  ]
};

export const stateCoordinates = [
  [
    "Alabama",
    "AL",
    "US",
    "y",
    "y",
    "alabama",
    32.806671,
    -86.79113,
    4779736,
    50744.0,
  ],
  [
    "Alaska",
    "AK",
    "US",
    "y",
    "n",
    "alaska",
    61.370716,
    -152.404419,
    710231,
    571951.25,
  ],
  [
    "Arizona",
    "AZ",
    "US",
    "y",
    "y",
    "arizona",
    33.729759,
    -111.431221,
    6392017,
    113634.57,
  ],
  [
    "Arkansas",
    "AR",
    "US",
    "y",
    "y",
    "arkansas",
    34.969704,
    -92.373123,
    2915918,
    52068.17,
  ],
  [
    "California",
    "CA",
    "US",
    "y",
    "y",
    "california",
    36.116203,
    -119.681564,
    37253956,
    155939.52,
  ],
  [
    "Colorado",
    "CO",
    "US",
    "y",
    "y",
    "colorado",
    39.059811,
    -105.311104,
    5029196,
    103717.53,
  ],
  [
    "Connecticut",
    "CT",
    "US",
    "y",
    "y",
    "connecticut",
    41.597782,
    -72.755371,
    3574097,
    4844.8,
  ],
  [
    "Delaware",
    "DE",
    "US",
    "y",
    "y",
    "delaware",
    39.318523,
    -75.507141,
    897934,
    1953.56,
  ],
  [
    "District of Columbia",
    "DC",
    "US",
    "n",
    "n",
    "district-of-columbia",
    38.897438,
    -77.026817,
    601723,
    68.34,
  ],
  [
    "Florida",
    "FL",
    "US",
    "y",
    "y",
    "florida",
    27.766279,
    -81.686783,
    18801310,
    53926.82,
  ],
  [
    "Georgia",
    "GA",
    "US",
    "y",
    "y",
    "georgia",
    33.040619,
    -83.643074,
    9687653,
    57906.14,
  ],
  [
    "Hawaii",
    "HI",
    "US",
    "y",
    "n",
    "hawaii",
    21.094318,
    -157.498337,
    1360301,
    6422.62,
  ],
  [
    "Idaho",
    "ID",
    "US",
    "y",
    "y",
    "idaho",
    44.240459,
    -114.478828,
    1567582,
    82747.21,
  ],
  [
    "Illinois",
    "IL",
    "US",
    "y",
    "y",
    "illinois",
    40.349457,
    -88.986137,
    12830632,
    55583.58,
  ],
  [
    "Indiana",
    "IN",
    "US",
    "y",
    "y",
    "indiana",
    39.849426,
    -86.258278,
    6483802,
    35866.9,
  ],
  [
    "Iowa",
    "IA",
    "US",
    "y",
    "y",
    "iowa",
    42.011539,
    -93.210526,
    3046355,
    55869.36,
  ],
  [
    "Kansas",
    "KS",
    "US",
    "y",
    "y",
    "kansas",
    38.5266,
    -96.726486,
    2853118,
    81814.88,
  ],
  [
    "Kentucky",
    "KY",
    "US",
    "y",
    "y",
    "kentucky",
    37.66814,
    -84.670067,
    4339367,
    39728.18,
  ],
  [
    "Louisiana",
    "LA",
    "US",
    "y",
    "y",
    "louisiana",
    31.169546,
    -91.867805,
    4533372,
    43561.85,
  ],
  [
    "Maine",
    "ME",
    "US",
    "y",
    "y",
    "maine",
    44.693947,
    -69.381927,
    1328361,
    30861.55,
  ],
  [
    "Maryland",
    "MD",
    "US",
    "y",
    "y",
    "maryland",
    39.063946,
    -76.802101,
    5773552,
    9773.82,
  ],
  [
    "Massachusetts",
    "MA",
    "US",
    "y",
    "y",
    "massachusetts",
    42.230171,
    -71.530106,
    6547629,
    7840.02,
  ],
  [
    "Michigan",
    "MI",
    "US",
    "y",
    "y",
    "michigan",
    43.326618,
    -84.536095,
    9883640,
    56803.82,
  ],
  [
    "Minnesota",
    "MN",
    "US",
    "y",
    "y",
    "minnesota",
    45.694454,
    -93.900192,
    5303925,
    79610.08,
  ],
  [
    "Mississippi",
    "MS",
    "US",
    "y",
    "y",
    "mississippi",
    32.741646,
    -89.678696,
    2967297,
    46906.96,
  ],
  [
    "Missouri",
    "MO",
    "US",
    "y",
    "y",
    "missouri",
    38.456085,
    -92.288368,
    5988927,
    68885.93,
  ],
  [
    "Montana",
    "MT",
    "US",
    "y",
    "y",
    "montana",
    46.921925,
    -110.454353,
    989415,
    145552.44,
  ],
  [
    "Nebraska",
    "NE",
    "US",
    "y",
    "y",
    "nebraska",
    41.12537,
    -98.268082,
    1826341,
    76872.41,
  ],
  [
    "Nevada",
    "NV",
    "US",
    "y",
    "y",
    "nevada",
    38.313515,
    -117.055374,
    2700551,
    109825.99,
  ],
  [
    "New Hampshire",
    "NH",
    "US",
    "y",
    "y",
    "new-hampshire",
    43.452492,
    -71.563896,
    1316470,
    8968.1,
  ],
  [
    "New Jersey",
    "NJ",
    "US",
    "y",
    "y",
    "new-jersey",
    40.298904,
    -74.521011,
    8791894,
    7417.34,
  ],
  [
    "New Mexico",
    "NM",
    "US",
    "y",
    "y",
    "new-mexico",
    34.840515,
    -106.248482,
    2059179,
    121355.53,
  ],
  [
    "New York",
    "NY",
    "US",
    "y",
    "y",
    "new-york",
    42.165726,
    -74.948051,
    19378102,
    47213.79,
  ],
  [
    "North Carolina",
    "NC",
    "US",
    "y",
    "y",
    "north-carolina",
    35.630066,
    -79.806419,
    9535483,
    48710.88,
  ],
  [
    "North Dakota",
    "ND",
    "US",
    "y",
    "y",
    "north-dakota",
    47.528912,
    -99.784012,
    672591,
    68975.93,
  ],
  [
    "Ohio",
    "OH",
    "US",
    "y",
    "y",
    "ohio",
    40.388783,
    -82.764915,
    11536504,
    40948.38,
  ],
  [
    "Oklahoma",
    "OK",
    "US",
    "y",
    "y",
    "oklahoma",
    35.565342,
    -96.928917,
    3751351,
    68667.06,
  ],
  [
    "Oregon",
    "OR",
    "US",
    "y",
    "y",
    "oregon",
    44.572021,
    -122.070938,
    3831074,
    95996.79,
  ],
  [
    "Pennsylvania",
    "PA",
    "US",
    "y",
    "y",
    "pennsylvania",
    40.590752,
    -77.209755,
    12702379,
    44816.61,
  ],
  [
    "Rhode Island",
    "RI",
    "US",
    "y",
    "y",
    "rhode-island",
    41.680893,
    -71.51178,
    1052567,
    1044.93,
  ],
  [
    "South Carolina",
    "SC",
    "US",
    "y",
    "y",
    "south-carolina",
    33.856892,
    -80.945007,
    4625364,
    30109.47,
  ],
  [
    "South Dakota",
    "SD",
    "US",
    "y",
    "y",
    "south-dakota",
    44.299782,
    -99.438828,
    814180,
    75884.64,
  ],
  [
    "Tennessee",
    "TN",
    "US",
    "y",
    "y",
    "tennessee",
    35.747845,
    -86.692345,
    6346105,
    41217.12,
  ],
  [
    "Texas",
    "TX",
    "US",
    "y",
    "y",
    "texas",
    31.054487,
    -97.563461,
    25145561,
    261797.12,
  ],
  [
    "Utah",
    "UT",
    "US",
    "y",
    "y",
    "utah",
    40.150032,
    -111.862434,
    2763885,
    82143.65,
  ],
  [
    "Vermont",
    "VT",
    "US",
    "y",
    "y",
    "vermont",
    44.045876,
    -72.710686,
    625741,
    9249.56,
  ],
  [
    "Virginia",
    "VA",
    "US",
    "y",
    "y",
    "virginia",
    37.769337,
    -78.169968,
    8001024,
    39594.07,
  ],
  [
    "Washington",
    "WA",
    "US",
    "y",
    "y",
    "washington",
    47.400902,
    -121.490494,
    6724540,
    66544.06,
  ],
  [
    "West Virginia",
    "WV",
    "US",
    "y",
    "y",
    "west-virginia",
    38.491226,
    -80.954453,
    1852994,
    24077.73,
  ],
  [
    "Wisconsin",
    "WI",
    "US",
    "y",
    "y",
    "wisconsin",
    44.268543,
    -89.616508,
    5686986,
    54310.1,
  ],
  [
    "Wyoming",
    "WY",
    "US",
    "y",
    "y",
    "wyoming",
    42.755966,
    -107.30249,
    563626,
    97100.4,
  ],
];
